import { useContext, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import useCreateReducer from '@/hooks/useCreateReducer';
import ChatbarContext from './Chatbar.context';
import { initialState } from './Chatbar.state';
import Icon from '@/components/redesign/icons/Icons';
import Logo from '@/components/redesign/Logo';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { deleteConversation, replaceConversations } from '@/libs/app/conversation';
import { saveFolders } from '@/libs/app/folders';
import { importData } from '@/libs/app/importExport';
import { getInitials } from '@/libs/helper';
import { t } from 'i18next';

export const Chatbar = () => {
  const historyBusyRef = useRef(false);
  const chatBarContextValue = useCreateReducer({
    initialState,
  });
  const {
    state: {
      conversations,
      showChatbar,
      selectedConversation,
      folders,
      actionsDisabled,
    },
    dispatch: homeDispatch,
    handleCreateFolder,
    handleNewConversation,
    createEmptyConversation,
    handleSelectConversation,
    handleUpdateConversation,
    handleUpdateFolder,
    handleDeleteFolder,
    handleExportData,
    restoreConversationHistory,
    discardPendingConversations,
    reportStorageFailure,
    setNotification,
    setStorageBusy,
  } = useChat();
  const { currentUser } = useAuth();
  const {
    state: { searchTerm, filteredConversations },
    dispatch: chatDispatch,
  } = chatBarContextValue;

  const handleImportConversations = async (data) => {
    if (actionsDisabled || historyBusyRef.current) return;
    historyBusyRef.current = true;
    setStorageBusy(true);
    try {
      const { history, folders, prompts, preferenceStorageError } = await importData(data, {
        onPreferenceSyncError: () =>
          setNotification({
            status: 'error',
            msg: t('Conversations were imported on this device, but account preferences could not be synced.'),
          }),
      });
      restoreConversationHistory(history, history[history.length - 1]?.id);
      homeDispatch({ field: 'folders', value: folders });
      homeDispatch({ field: 'prompts', value: prompts });
      if (preferenceStorageError) {
        setNotification({
          status: 'error',
          msg: t('Conversations were imported. Some folders or prompts could not be saved on this device. Export a backup before reloading to keep them.'),
        });
      }
    } catch (error) {
      reportStorageFailure(error);
      setNotification({ status: 'error', msg: t('Could not import conversations. Please check the file and try again.') });
    } finally {
      historyBusyRef.current = false;
      setStorageBusy(false);
    }
  };

  const emptyConversation = () => createEmptyConversation({
    model: selectedConversation?.model,
    temperature: selectedConversation?.temperature,
    mode: selectedConversation?.mode,
  });

  const handleClearConversations = async () => {
    if (actionsDisabled || historyBusyRef.current) return;
    historyBusyRef.current = true;
    setStorageBusy(true);
    try {
      await replaceConversations([]);
      discardPendingConversations();
      homeDispatch({ field: 'selectedConversation', value: emptyConversation() });
      homeDispatch({ field: 'conversations', value: [] });
      const updatedFolders = folders.filter((f) => f.type !== 'chat');
      homeDispatch({ field: 'folders', value: updatedFolders });
      saveFolders(updatedFolders);
    } catch (error) {
      reportStorageFailure(error);
    } finally {
      historyBusyRef.current = false;
      setStorageBusy(false);
    }
  };

  const handleDeleteConversation = async (conversation) => {
    if (actionsDisabled || historyBusyRef.current) return;
    historyBusyRef.current = true;
    setStorageBusy(true);
    try {
      await deleteConversation(conversation.id);
      discardPendingConversations([conversation.id]);
      homeDispatch((state) => {
        const remaining = state.conversations.filter((c) => c.id !== conversation.id);
        return { ...state, conversations: remaining,
          selectedConversation: state.selectedConversation?.id === conversation.id
            ? remaining[remaining.length - 1] || emptyConversation()
            : state.selectedConversation };
      });
      chatDispatch({ field: 'searchTerm', value: '' });
    } catch (error) {
      reportStorageFailure(error);
    } finally {
      historyBusyRef.current = false;
      setStorageBusy(false);
    }
  };

  const handleToggleChatbar = () => {
    homeDispatch({ field: 'showChatbar', value: !showChatbar });
    localStorage.setItem('showChatbar', JSON.stringify(!showChatbar));
  };

  const handleDrop = (e) => {
    if (e.dataTransfer) {
      const conversation = JSON.parse(e.dataTransfer.getData('conversation'));
      handleUpdateConversation(conversation, { key: 'folderId', value: 0 });
      chatDispatch({ field: 'searchTerm', value: '' });
      e.target.style.removeProperty('background');
    }
  };

  const allowDrop = (e) => e.preventDefault();

  useEffect(() => {
    if (searchTerm) {
      chatDispatch({
        field: 'filteredConversations',
        value: conversations.filter((conversation) => {
          const searchable =
            conversation.name.toLocaleLowerCase() +
            ' ' +
            conversation.messages.map((message) => message.content).join(' ');
          return searchable.toLowerCase().includes(searchTerm.toLowerCase());
        }),
      });
    } else {
      chatDispatch({
        field: 'filteredConversations',
        value: conversations,
      });
    }
  }, [searchTerm, conversations, chatDispatch]);

  // Group conversations by folder
  const chatFolders = folders.filter((f) => f.type === 'chat');
  const folderIds = chatFolders.map((f) => f.id);
  const unfiledConversations = filteredConversations
    .filter((c) => !c.folderId || !folderIds.includes(c.folderId))
    .slice()
    .reverse();

  const userInitials = getInitials(currentUser?.name);

  /* ── Collapsed state ── */
  if (!showChatbar) {
    return (
      <aside className="hidden w-14 flex-shrink-0 flex-col items-center gap-1.5 border-r border-border bg-surface-paper-2 py-3 md:flex">
        <button
          onClick={handleToggleChatbar}
          aria-label="Open conversations sidebar"
          className="grid h-9 w-9 place-items-center rounded-lg text-ink-600 hover:bg-ink-100"
        >
          <Icon.Sidebar width={18} height={18} />
        </button>
        <button
          onClick={handleNewConversation}
          disabled={actionsDisabled}
          aria-label="New consultation"
          className="btn-redesign btn-redesign-primary grid h-9 w-9 place-items-center !rounded-lg !p-0"
        >
          <Icon.Plus width={18} height={18} />
        </button>
        <div className="mx-auto my-1 h-px w-7 bg-border" />
        {chatFolders.slice(0, 5).map((f) => (
          <div
            key={f.id}
            title={f.name}
            className="grid h-9 w-9 place-items-center rounded-lg text-ink-600"
            aria-hidden="true"
          >
            <span
              className="block h-2 w-2 rounded-sm"
              style={{ background: f.color || 'var(--ink-400)' }}
            />
          </div>
        ))}
        <div className="mt-auto" />
        <Link
          href="/setting"
          className="grid h-9 w-9 place-items-center rounded-full bg-brand-800 text-[11px] font-semibold text-white"
        >
          {userInitials}
        </Link>
      </aside>
    );
  }

  /* ── Expanded state ── */
  return (
    <ChatbarContext.Provider
      value={{
        ...chatBarContextValue,
        handleDeleteConversation,
        handleClearConversations,
        handleImportConversations,
        handleExportData,
      }}
    >
      {/* Mobile drawer backdrop */}
      <div
        onClick={handleToggleChatbar}
        className="fixed inset-0 z-40 animate-[drawerBackdropIn_0.2s_ease] bg-ink-900/40 backdrop-blur-sm md:hidden"
      />
      <aside className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-shrink-0 animate-[drawerInLeft_0.22s_ease] flex-col border-r border-border bg-surface-paper-2 shadow-xl md:static md:z-auto md:max-w-none md:animate-none md:shadow-none">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 pb-2.5 pt-3.5">
          <Link href="/">
            <Logo size={24} />
          </Link>
          <button
            onClick={handleToggleChatbar}
            aria-label={t('Collapse conversations sidebar')}
            className="ml-auto grid h-7 w-7 place-items-center rounded-md text-ink-500 hover:bg-ink-100"
          >
            <Icon.Sidebar width={16} height={16} />
          </button>
        </div>

        {/* New + search */}
        <div className="px-3 pb-2.5">
          <button
            disabled={actionsDisabled}
            onClick={() => {
              handleNewConversation();
              chatDispatch({ field: 'searchTerm', value: '' });
            }}
            className="btn-redesign btn-redesign-primary w-full justify-start px-3 py-2.5 text-[13px]"
          >
            <Icon.Plus width={15} height={15} />
            {t('New consultation')}
          </button>
          <div className="relative mt-2">
            <Icon.Search
              width={14}
              height={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <input
              value={searchTerm}
              onChange={(e) =>
                chatDispatch({ field: 'searchTerm', value: e.target.value })
              }
              placeholder={t('Search conversations')}
              className="w-full rounded-md border border-border bg-white py-[7px] pl-[30px] pr-3 font-sans text-[12.5px] text-ink-800 placeholder:text-ink-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </div>

        {/* Folders */}
        <div className="px-3 pb-2.5">
          <div className="flex items-center px-1 pb-1.5 pt-2">
            <span className="t-eyebrow text-[10.5px]">{t('Folders')}</span>
            <button
              onClick={() => handleCreateFolder(t('New folder'), 'chat')}
              aria-label={t('New folder')}
              className="ml-auto text-ink-400 hover:text-ink-600"
            >
              <Icon.Plus width={12} height={12} />
            </button>
          </div>
          {chatFolders.length > 0 && (
            <div className="grid gap-px">
              {chatFolders
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((f) => {
                  const count = conversations.filter(
                    (c) => c.folderId === f.id,
                  ).length;
                  return (
                    <FolderRow
                      key={f.id}
                      folder={f}
                      count={count}
                      onDrop={(conversation) =>
                        handleUpdateConversation(conversation, { key: 'folderId', value: f.id })
                      }
                      onRename={(name) => handleUpdateFolder(f.id, name)}
                      onDelete={() => handleDeleteFolder(f.id)}
                      deleteDisabled={actionsDisabled}
                    />
                  );
                })}
            </div>
          )}
        </div>

        {/* Conversation list */}
        <div
          className="flex-1 overflow-y-auto px-2 pb-3"
          onDrop={handleDrop}
          onDragOver={allowDrop}
        >
          {/* Folder-grouped conversations */}
          {chatFolders.map((folder) => {
            const folderConvos = filteredConversations
              .filter((c) => c.folderId === folder.id)
              .slice()
              .reverse();
            if (folderConvos.length === 0) return null;
            return (
              <div key={folder.id}>
                <div className="px-2 pb-1 pt-3.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-400">
                  {folder.name}
                </div>
                {folderConvos.map((c) => (
                  <ConversationItem
                    key={c.id}
                    conversation={c}
                    isActive={selectedConversation?.id === c.id}
                    onSelect={() => handleSelectConversation(c)}
                    actionsDisabled={actionsDisabled}
                  />
                ))}
              </div>
            );
          })}

          {/* Unfiled conversations */}
          {unfiledConversations.length > 0 && (
            <div>
              {chatFolders.length > 0 && (
                <div className="px-2 pb-1 pt-3.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-400">
                  {t('Unfiled')}
                </div>
              )}
              {unfiledConversations.map((c) => (
                <ConversationItem
                  key={c.id}
                  conversation={c}
                  isActive={selectedConversation?.id === c.id}
                  onSelect={() => handleSelectConversation(c)}
                  actionsDisabled={actionsDisabled}
                />
              ))}
            </div>
          )}

          {filteredConversations.length === 0 && (
            <div className="mt-8 text-center text-[13px] text-ink-400">
              {t('No data.')}
            </div>
          )}
        </div>

        {/* Credits + user footer */}
        <div className="border-t border-border bg-surface-paper-2 p-3">
          {/* Credits bar */}
          {currentUser && (
            <div className="mb-2.5 rounded-lg border border-border bg-white px-3 py-2.5">
              <div className="mb-1.5 flex items-baseline justify-between">
                <span className="text-[11px] font-medium text-ink-600">
                  {t('Credits')} ·{' '}
                  {currentUser.subscriptions?.premium || currentUser.subscriptions?.professional
                    ? 'Premium'
                    : 'Free'}
                </span>
                <span className="t-num text-[11px] text-ink-500">
                  {(currentUser.credits || 0).toLocaleString()}
                </span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-ink-100">
                <div
                  className="h-full bg-teal-700"
                  style={{
                    width: `${Math.min(100, ((currentUser.credits || 0) / 8000) * 100)}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Data actions */}
          <div className="flex items-center gap-1 border-t border-border pt-2">
            <label
              title={t('Import conversations')}
              className="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-[11px] text-ink-500 hover:bg-ink-100"
            >
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    try {
                      handleImportConversations(JSON.parse(ev.target.result));
                    } catch {
                      // ignore malformed JSON
                    }
                  };
                  reader.readAsText(file);
                  e.target.value = '';
                }}
              />
              {t('Import')}
            </label>
            <button
              onClick={handleExportData}
              title={t('Export conversations')}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-ink-500 hover:bg-ink-100"
            >
{t('Export')}
            </button>
            <button
              onClick={() => {
                if (actionsDisabled) return;
                if (window.confirm(t('Are you sure you want to clear all conversations?'))) {
                  handleClearConversations();
                }
              }}
              disabled={actionsDisabled}
              title={t('Clear all conversations')}
              className="ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-status-red-700 hover:bg-status-red-100/50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Icon.Trash width={12} height={12} /> {t('Clear')}
            </button>
          </div>

          {/* App nav links */}
          <div className="flex gap-1 border-t border-border pt-2">
            <Link href="/diagnose" className="flex flex-1 items-center justify-center gap-1 rounded-md py-1.5 text-[11px] text-ink-600 hover:bg-ink-100">
              <Icon.Stethoscope width={12} height={12} /> {t('Diagnose')}
            </Link>
            <Link href="/side-by-side" className="flex flex-1 items-center justify-center gap-1 rounded-md py-1.5 text-[11px] text-ink-600 hover:bg-ink-100">
              <Icon.Compare width={12} height={12} /> {t('Compare')}
            </Link>
          </div>

          {/* User row */}
          <Link
            href="/setting"
            className="flex items-center gap-2.5 rounded-md px-1.5 py-1.5 hover:bg-ink-100"
          >
            <div className="grid h-[30px] w-[30px] place-items-center rounded-full bg-brand-800 text-[11px] font-semibold text-white">
              {userInitials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-semibold text-ink-900">
                {currentUser?.name || 'User'}
              </div>
              <div className="truncate text-[10.5px] text-ink-500">
                {currentUser?.email || ''}
              </div>
            </div>
            <Icon.Settings width={14} height={14} className="text-ink-400" />
          </Link>
        </div>
      </aside>
    </ChatbarContext.Provider>
  );
};

/* Inline rename field shared by folders and conversations */
function RenameInput({ value, onChange, onCommit, onCancel, className }) {
  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommit();
        if (e.key === 'Escape') onCancel();
      }}
      className={className}
    />
  );
}

/* Conversation list item with redesign styling */
function FolderRow({ folder, count, onDrop, onRename, onDelete, deleteDisabled }) {
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(folder.name);

  const commitRename = () => {
    if (renameValue.trim() && renameValue !== folder.name) onRename(renameValue.trim());
    setRenaming(false);
  };

  return (
    <div
      tabIndex={0}
      className="group flex cursor-pointer items-center gap-2 rounded-[5px] px-2 py-[5px] text-[12.5px] text-ink-700 hover:bg-ink-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-500"
      onDrop={(e) => {
        e.stopPropagation();
        if (e.dataTransfer) {
          const conversation = JSON.parse(e.dataTransfer.getData('conversation'));
          onDrop(conversation);
        }
      }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      <Icon.Folder width={13} height={13} style={{ color: folder.color || 'var(--ink-400)' }} />
      {renaming ? (
        <RenameInput
          value={renameValue}
          onChange={setRenameValue}
          onCommit={commitRename}
          onCancel={() => setRenaming(false)}
          className="flex-1 rounded border border-brand-500 bg-white px-1 text-[12.5px] outline-none"
        />
      ) : (
        <span className="flex-1 truncate">{folder.name}</span>
      )}
      <span className="font-mono text-[11px] text-ink-400">{count}</span>
      <div className="invisible flex gap-0.5 group-hover:visible group-focus-within:visible">
        <button onClick={(e) => { e.stopPropagation(); setRenaming(true); setRenameValue(folder.name); }} className="p-0.5 text-ink-400 hover:text-ink-700" aria-label="Rename folder">
          <Icon.Edit width={10} height={10} />
        </button>
        <button disabled={deleteDisabled} onClick={(e) => { e.stopPropagation(); if (window.confirm(t('Delete this folder?'))) onDelete(); }} className="p-0.5 text-ink-400 hover:text-status-red-500 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Delete folder">
          <Icon.Trash width={10} height={10} />
        </button>
      </div>
    </div>
  );
}

function ConversationItem({
  conversation,
  isActive,
  onSelect,
  actionsDisabled,
}) {
  const { handleDeleteConversation } = useContext(ChatbarContext);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(conversation.name);
  const { handleUpdateConversation } = useChat();

  const commitRename = () => {
    if (renameValue.trim() && renameValue !== conversation.name) {
      handleUpdateConversation(conversation, { key: 'name', value: renameValue.trim() });
    }
    setRenaming(false);
  };

  return (
    <div
      draggable={!actionsDisabled}
      onDragStart={(e) => {
        if (actionsDisabled || !e.dataTransfer) return;
        e.dataTransfer.setData('conversation', JSON.stringify(conversation));
      }}
      className={`group mb-px rounded-md px-2.5 py-2 ${
        isActive
          ? 'border border-brand-200 bg-brand-50'
          : 'border border-transparent hover:bg-ink-100'
      }`}
    >
      <div className="flex items-center gap-1.5">
        {renaming ? (
          <RenameInput
            value={renameValue}
            onChange={setRenameValue}
            onCommit={commitRename}
            onCancel={() => setRenaming(false)}
            className="flex-1 rounded border border-brand-500 bg-white px-1 text-[12.5px] text-ink-900 outline-none"
          />
        ) : (
          <button
            onClick={onSelect}
            disabled={actionsDisabled}
            className={`flex-1 truncate text-left text-[12.5px] disabled:cursor-not-allowed ${
              isActive ? 'font-semibold text-brand-800' : 'font-medium text-ink-800'
            }`}
          >
            {conversation.name}
          </button>
        )}
        {!actionsDisabled && (
          <div className="invisible flex gap-0.5 group-hover:visible group-focus-within:visible">
            <button
              onClick={(e) => { e.stopPropagation(); setRenaming(true); setRenameValue(conversation.name); }}
              className="p-0.5 text-ink-400 hover:text-ink-700"
              aria-label="Rename conversation"
            >
              <Icon.Edit width={11} height={11} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); if (window.confirm(t('Delete this conversation?'))) handleDeleteConversation(conversation); }}
              className="p-0.5 text-ink-400 hover:text-status-red-500"
              aria-label="Delete conversation"
            >
              <Icon.Trash width={11} height={11} />
            </button>
          </div>
        )}
      </div>
      <div className="mt-0.5 text-[10.5px] text-ink-400">
        {conversation.model || ''}
      </div>
    </div>
  );
}
